/**
 * THE STONE VOCABULARY — one list, both surfaces.
 *
 * ⚠️ THE DRIFT THIS FILE EXISTS TO END. The Builder and the Composer each
 * carried their own idea of what a stone IS, and they were not two spellings of
 * one list — they were two different models:
 *
 *   Builder  (studioConfig.js)  shape + setting + package
 *                               princess-cut · round-brilliant · … / genuine · economy
 *   Composer (engine/stones.js) gem type + setting + an exact color override
 *                               diamond · ruby · sapphire · onyx · brand
 *
 * Metals, finishes and stone SETTINGS had already been reconciled onto one set
 * of ids, deliberately, so the two surfaces could not describe the same piece
 * two ways. Stones were the axis nobody finished, and the consequence is an
 * order: two operators configuring the same ring on two tabs of the SAME PAGE
 * produce records that cannot be compared, because one says "pavé princess-cut,
 * genuine" and the other says "pavé ruby #a1121f".
 *
 * SO THE FOUR AXES LIVE HERE, ONCE, and both surfaces import them. Neither file
 * may declare a stone list literal again — `verify-references.mjs` §10 asserts
 * that against the real files, in both directions.
 *
 * ---------------------------------------------------------------------------
 * WHY THE AXES ARE FOUR AND NOT ONE
 *
 * They are not rival vocabularies for one attribute; they are four independent
 * attributes, and each surface was missing some of them. Collapsing them into a
 * single list would be the same mistake in the other direction.
 *
 *   TYPE      what the stone is made of        ruby, diamond, the brand color
 *   SHAPE     how it is cut                    princess, baguette, marquise
 *   SETTING   how it sits in the metal         channel, pavé, halo, picture-frame
 *   PACKAGE   the commercial grade             genuine, economy
 *
 * ---------------------------------------------------------------------------
 * ⚠️ ONE AXIS IS STILL SURFACE-SPECIFIC, AND IT IS SAID OUT LOUD RATHER THAN
 * FAKED. `supports` records which axes each surface can actually ACT on:
 *
 *   composer  type · setting            (it renders PBR material and a color)
 *   builder   shape · setting · package (the render contract's `components.stones`)
 *
 * The Builder cannot express TYPE because `POST /api/artwork/render` has no
 * stone-material slot — `promptAssembler` reads `stones{shapes,setting,package}`
 * and nothing else. Shipping a stone-type chip in the Builder would post a
 * field the engine drops on the floor, which is worse than the gap: the
 * operator would believe they had specified a ruby. So the chip is not there,
 * and the missing slot is a NAMED item in the backend handover
 * (`docs/BACKEND-HANDOVER.md` §2) rather than a control that does nothing.
 *
 * The moment /config serves `stoneTypes`, `optionsFor()` picks it up and the
 * Builder gains the axis with no change to this file.
 */

/** What the stone is made of. Drives the Composer's PBR accent material. */
export const STONE_TYPES = [
  { id: 'none', label: 'None', color: '', kind: 'none' },
  { id: 'diamond', label: 'Diamond / CZ', color: '#ffffff', kind: 'diamond' },
  { id: 'ruby', label: 'Ruby', color: '#a1121f', kind: 'gem' },
  { id: 'sapphire', label: 'Sapphire', color: '#153a8a', kind: 'gem' },
  { id: 'emerald', label: 'Emerald', color: '#0f6b45', kind: 'gem' },
  { id: 'onyx', label: 'Onyx', color: '#141416', kind: 'gem' },
  { id: 'amethyst', label: 'Amethyst', color: '#5b2d8e', kind: 'gem' },
  { id: 'topaz', label: 'Topaz', color: '#d99a24', kind: 'gem' },
  { id: 'brand', label: 'Brand color stone', color: '', kind: 'gem' },
]

/** How the stone is cut. */
export const STONE_SHAPES = [
  { id: 'princess-cut', label: 'Princess-cut' },
  { id: 'round-brilliant', label: 'Round-brilliant' },
  { id: 'baguette', label: 'Baguette' },
  { id: 'marquise', label: 'Marquise' },
  { id: 'emerald-cut', label: 'Emerald-cut' },
  { id: 'oval', label: 'Oval' },
  { id: 'pear', label: 'Pear' },
]

/**
 * How the stone sits in the metal. `prong` / `bezel set` / `flush / gypsy` are
 * RETIRED — see `studioConfig.js#LEGACY_ALIASES.stoneSettings`, which maps the
 * old words onto these rather than rendering them.
 */
export const STONE_SETTINGS = [
  { id: 'channel', label: 'Channel-set' },
  { id: 'pavé', label: 'Pavé' },
  { id: 'halo', label: 'Halo' },
  { id: 'picture-frame', label: 'Picture-frame border' },
]

/** The commercial grade. */
export const STONE_PACKAGES = [
  { id: 'genuine', label: 'Genuine' },
  { id: 'economy', label: 'Economy' },
]

/** The default setting — first in the SR list, never a retired one. */
export const DEFAULT_STONE_SETTING = STONE_SETTINGS[0].id

/** Which axes each surface can act on. Asserted, not decorative. */
export const SUPPORTS = {
  composer: ['type', 'setting'],
  builder: ['shape', 'setting', 'package'],
}

const byId = (list) => Object.fromEntries(list.map((x) => [x.id, x]))
const labels = (list) => Object.fromEntries(list.map((x) => [x.id, x.label]))

export const STONE_TYPE_BY_ID = byId(STONE_TYPES)
export const STONE_SETTING_BY_ID = byId(STONE_SETTINGS)

/** id → SR display term, per axis. Consumed by `studioConfig.js#SR_LABELS`. */
export const STONE_LABELS = {
  stoneTypes: labels(STONE_TYPES),
  stoneShapes: labels(STONE_SHAPES),
  stoneSettings: labels(STONE_SETTINGS),
  stonePackages: labels(STONE_PACKAGES),
}

/** The bare id lists, for the config layer's option arrays. */
export const STONE_IDS = {
  stoneTypes: STONE_TYPES.map((x) => x.id),
  stoneShapes: STONE_SHAPES.map((x) => x.id),
  stoneSettings: STONE_SETTINGS.map((x) => x.id),
  stonePackages: STONE_PACKAGES.map((x) => x.id),
}
